import React, { ReactElement, useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { useForm } from 'react-hook-form';
import { deleteField, serverTimestamp, updateDoc } from 'firebase/firestore';
import { reportError } from 'lib/util';
import { usePrevious } from 'lib/usePrevious';
import LinkDriveButton from 'components/link-drive-button';
import { FileDoc, User } from 'lib/model';
import TransitionDialog from 'components/transition-dialog';
import {
  ExclamationIcon,
  InformationCircleIcon,
  TrashIcon,
} from '@heroicons/react/solid';
import { User as AuthUser } from '@firebase/auth';

interface FormFields {
  confirmDeletion: boolean;
}

export default function FileDeleteDialog({
  open,
  setOpen,
  file,
  user,
  authUser,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  file: FileDoc;
  user: User;
  authUser: AuthUser;
}): ReactElement {
  const previousOpen = usePrevious(open);
  const cancelButtonRef = useRef(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormFields>();

  useEffect(() => {
    if (!previousOpen && open) {
      setValue('confirmDeletion', false);
    }
  }, [open, previousOpen, setValue]);

  async function triggerFileDeletion() {
    try {
      updateDoc(file.ref, {
        deleteTriggered: serverTimestamp(),
        deleteError: deleteField(),
      });
      setOpen(false);
    } catch (e) {
      reportError(e);
    }
  }

  const transcriptIsOld = file?.created < new Date('2022-07-01');
  const driveNeedsLinking = transcriptIsOld && user && !user.tokens;

  return (
    <TransitionDialog open={open} initialFocus={cancelButtonRef}>
      <form onSubmit={handleSubmit(triggerFileDeletion)}>
        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
              <TrashIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
            </div>
            <div className="mt-3 text-center min-w-0 sm:mt-0 sm:ml-4 sm:text-left">
              <Dialog.Title
                as="h3"
                className="text-lg leading-6 font-medium text-gray-900"
              >
                Delete
              </Dialog.Title>
              <div className="mt-2">
                {driveNeedsLinking ? (
                  <LinkDriveButton user={user} authUser={authUser} />
                ) : (
                  <>
                    <p>You are about to remove this file from TranscribeKit</p>
                    <p className="mt-2 font-bold text-lg break-words">
                      {file.name}
                    </p>
                    <p className="mt-2">
                      <ExclamationIcon className="inline w-5 h-5 mr-2 text-red-600" />
                      Deleted transcripts cannot be recovered
                    </p>
                    <p>
                      <InformationCircleIcon className="inline w-5 h-5 mr-2 text-blue-600" />
                      Exported <i>Google Docs</i> will <b>not</b> be deleted
                    </p>
                    {transcriptIsOld && (
                      <p>
                        <InformationCircleIcon className="inline w-5 h-5 mr-2 text-blue-600" />
                        Original <i>Google Doc</i> will be backed up to the{' '}
                        <b>TranscribeKit Transcripts</b> folder in your{' '}
                        <i>Drive</i>
                      </p>
                    )}
                    <div className="mt-4 flex items-start">
                      <div className="flex items-end h-5">
                        <input
                          id="confirmDeletion"
                          name="confirmDeletion"
                          type="checkbox"
                          {...register('confirmDeletion', {
                            validate: (value) =>
                              value === true || 'Please check the box',
                          })}
                          className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-40"
                        />
                      </div>
                      <div className="ml-3">
                        <label
                          htmlFor="confirmDeletion"
                          className="font-medium text-gray-700 disabled:opacity-40"
                        >
                          I confirm that I want to remove this file
                        </label>
                        {errors.confirmDeletion && (
                          <p className="text-red-500">
                            {errors.confirmDeletion.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="submit"
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-40 sm:ml-3 sm:w-auto sm:text-sm"
            disabled={!file || !user || driveNeedsLinking}
          >
            Delete
          </button>
          <button
            type="button"
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={() => setOpen(false)}
            ref={cancelButtonRef}
          >
            Cancel
          </button>
        </div>
      </form>
    </TransitionDialog>
  );
}
